'use client';

import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import {
  type CartItem,
  loadCartFromCookie,
  saveCartToCookie,
  canAddCartItem,
  reconcileCartWithOwnedItems,
} from '@/lib/cart-storage';
import { syncCart } from '@/lib/cart-sync';
import { useOwnedItems } from './OwnedItemsContext';

export type { CartItem };

// Debounce window for the background cart-sync side effect — avoids firing a request per
// keystroke-speed add/remove burst. Purely an analytics side effect; never blocks cart UX.
const CART_SYNC_DEBOUNCE_MS = 2000;

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (moduleId: string) => void;
  clearCart: () => void;
  isInCart: (moduleId: string) => boolean;
  totalAmount: number;
  itemCount: number;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  /** Fire-and-forget: tell the backend a checkout was just initiated for the current cart. */
  notifyCheckoutInitiated: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const { ownedModuleIds, ownedCourseIds, ownedBundleIds, isLoaded: ownedLoaded } = useOwnedItems();

  const hydrate = useCallback(() => {
    setItems(loadCartFromCookie());
  }, []);

  // Hydrate from the aks_cart cookie on mount.
  useEffect(() => {
    hydrate();
    setHydrated(true);
  }, [hydrate]);

  // Cookies don't fire a `storage` event the way localStorage did, so
  // cross-tab/cross-app reactivity (e.g. dashboard clearing the cart after a
  // purchase) is picked up by re-reading on focus and visibility change
  // instead. Real-time push sync is dropped as a non-critical nice-to-have —
  // see docs/superpowers/specs/2026-07-18-session-cookie-auth-design.md §6.
  useEffect(() => {
    const handleRefresh = () => hydrate();
    window.addEventListener('focus', handleRefresh);
    document.addEventListener('visibilitychange', handleRefresh);
    return () => {
      window.removeEventListener('focus', handleRefresh);
      document.removeEventListener('visibilitychange', handleRefresh);
    };
  }, [hydrate]);

  // Auto-open cart drawer if redirected back with openCart param.
  //
  // Reads window.location.search rather than useSearchParams(). That hook opts the calling
  // component out of server rendering, and because CartProvider wraps {children} in the root
  // layout, its Suspense boundary took the whole page down with it — every route on the site
  // was served as a 122-byte BAILOUT_TO_CLIENT_SIDE_RENDERING shell with no markup for
  // crawlers and nothing to paint until JS booted. This effect is already client-only (gated
  // on `hydrated`) and already reads window.location below, so the hook bought nothing.
  useEffect(() => {
    if (!hydrated) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('openCart') !== 'true') return;

    setIsDrawerOpen(true);
    url.searchParams.delete('openCart');
    window.history.replaceState({}, '', url.toString());
  }, [hydrated]);

  // Reconcile against ownership once the OwnedItemsContext fetch resolves (and
  // again any time it changes, e.g. after a checkout success refresh()) — drops
  // any module/course/bundle the user already owns instead of leaving it in the
  // cart forever inflating the header badge. Runs after hydrate so it never
  // fires against the empty pre-hydration state and wipes a real cart.
  useEffect(() => {
    if (!hydrated || !ownedLoaded) return;
    setItems((prev) => {
      const reconciled = reconcileCartWithOwnedItems(prev, {
        moduleIds: ownedModuleIds,
        courseIds: ownedCourseIds,
        bundleIds: ownedBundleIds,
      });
      return reconciled.length === prev.length ? prev : reconciled;
    });
  }, [hydrated, ownedLoaded, ownedModuleIds, ownedCourseIds, ownedBundleIds]);

  // Persist on change (skip initial hydration)
  useEffect(() => {
    if (!hydrated) return;
    saveCartToCookie(items);
  }, [items, hydrated]);

  // Fire-and-forget backend sync, debounced — a pure analytics side effect layered on top of
  // the cookie persistence above, never blocking or affecting cart UX. Empty cart (explicit
  // clear, or the last item removed) reports CLEARED instead of ACTIVE.
  useEffect(() => {
    if (!hydrated) return;
    const timer = setTimeout(() => {
      syncCart(items, items.length === 0 ? 'CLEARED' : 'ACTIVE');
    }, CART_SYNC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [items, hydrated]);

  const addItem = useCallback((item: CartItem) => {
    setItems((prev) => {
      if (prev.some((i) => i.moduleId === item.moduleId)) return prev;
      if (!canAddCartItem(prev)) return prev;
      return [...prev, item];
    });
  }, []);

  const removeItem = useCallback((moduleId: string) => {
    setItems((prev) => prev.filter((i) => i.moduleId !== moduleId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const isInCart = useCallback(
    (moduleId: string) => items.some((i) => i.moduleId === moduleId),
    [items]
  );

  const totalAmount = useMemo(
    () => items.reduce((sum, i) => sum + i.price, 0),
    [items]
  );

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  // Immediate (non-debounced) — checkout initiation is a discrete event, not a state change
  // to react to, so it doesn't go through the items-change effect above.
  const notifyCheckoutInitiated = useCallback(() => {
    syncCart(items, 'CHECKED_OUT');
  }, [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        clearCart,
        isInCart,
        totalAmount,
        itemCount: items.length,
        isDrawerOpen,
        openDrawer,
        closeDrawer,
        notifyCheckoutInitiated,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}
